import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import unsamLogo from "../../assets/img/unsam.png";
import garudaLogo from "../../assets/img/garuda.png";
import scopusLogo from "../../assets/img/scopus.png";
import googleScholarLogo from "../../assets/img/googleScholar.png";
import thomsonLogo from "../../assets/img/thomsonReuters.png";
import texturedBackground from "../../assets/img/bg.png";
import { apiContext } from "../../api";
import PieChartPublikasi from "../component/PieChartPublikasi";
import BarChartQuartile from "../component/BarChartQuartile";

function fullName(user) {
  return `${user?.name || "Lecturer Profile"}`;
}

function getPhotoUrl(user, API_URL) {
  const foto = user?.profile?.foto;
  if (!foto) {
    return null;
  }

  return foto.startsWith("http") ? foto : `${API_URL}/${foto}`;
}

function getPublicationTitle(item) {
  return item?.judul || "No title";
}

function getPublicationYear(item) {
  if (item?.tahun) {
    return item.tahun;
  }

  if (!item?.tanggal) {
    return "";
  }

  const date = new Date(item.tanggal);
  return Number.isNaN(date.getFullYear()) ? "" : date.getFullYear();
}

function getPublicationType(item) {
  return item?.jenis_publikasi?.nama || "Publikasi";
}

function getPublicationVenue(item) {
  return item?.detail_publikasi?.nama_jurnal || "-";
}

function getPublicationLink(item) {
  const link = item?.detail_publikasi?.tautan;

  if (link) {
    return link;
  }

  return item?.doi ? `https://doi.org/${item.doi}` : "";
}

function ProfileBadge({ href, image, label }) {
  if (!href) {
    return null;
  }

  return (
    <a
      className="fsd-profile-badge rounded-pill"
      href={href}
      target="_blank"
      rel="noreferrer"
      title={label}
    >
      <img src={image} alt={label} />
    </a>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="fsd-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function formatPublicationFSD(item) {
  const origin = (value) => {
    const valUpper = value?.toUpperCase();
    if (valUpper === "SINTA")
      return (
        <img
          src={garudaLogo}
          alt="sinta"
          style={{ height: "20px", width: "auto", objectFit: "contain" }}
        />
      );
    if (valUpper === "SCOPUS")
      return (
        <img
          src={scopusLogo}
          alt="scopus"
          style={{ height: "20px", width: "auto", objectFit: "contain" }}
        />
      );
    if (valUpper === "WEB OF SCIENCE")
      return (
        <img
          src={thomsonLogo}
          alt="web of science"
          style={{ height: "20px", width: "auto", objectFit: "contain" }}
        />
      );
    if (valUpper === "GOOGLE SCHOLAR")
      return (
        <img
          src={googleScholarLogo}
          alt="google scholar"
          style={{ height: "20px", width: "auto", objectFit: "contain" }}
        />
      );
    return value || "Manual";
  };

  const title = getPublicationTitle(item);
  const link = getPublicationLink(item);
  const year = getPublicationYear(item);
  const venue = getPublicationVenue(item);
  const quartile = item?.quartile || item?.detail_publikasi?.quartile;

  let authors = "Unknown author";
  if (Array.isArray(item?.penulis) && item.penulis.length > 0) {
    authors = item.penulis
      .map((author) => author?.user?.name || author?.nama || author?.user?.nama)
      .join(", ");
  } else if (item?.penulis && typeof item.penulis === "string") {
    authors = item.penulis;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          style={{
            fontWeight: "800",
            color: "#106fa0",
            textDecoration: "none",
            marginBottom: "4px",
            fontSize: "15px",
          }}
        >
          {title}
        </a>
      ) : (
        <span
          style={{
            fontWeight: "800",
            color: "#112f4a",
            marginBottom: "8px",
            fontSize: "15px",
          }}
        >
          {title}
        </span>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "14px",
          alignItems: "center",
          marginTop: "6px",
        }}
      >
        <span
          style={{
            border: "1px solid #0ea5e9",
            color: "#0ea5e9",
            padding: "2px 10px",
            borderRadius: "9999px",
            fontSize: "11px",
            display: "inline-flex",
            alignItems: "center",
            height: "26px",
          }}
        >
          {origin(item?.asal_data || item?.detail_publikasi?.asal_data)}
        </span>

        <span style={{ color: "#475569", fontSize: "13px" }}>
          Year: {year || "-"}
        </span>

        {quartile != null ? (
          <span
            style={{ color: "#0284c7", fontSize: "13px", fontWeight: "bold" }}
          >
            Q-{quartile}
          </span>
        ) : (
          <span
            style={{ color: "#0284c7", fontSize: "13px", fontWeight: "bold" }}
          >
            No-Q
          </span>
        )}

        {venue && venue !== "-" && (
          <span style={{ color: "#64748b", fontSize: "13px" }}>
            Journal: {venue}
          </span>
        )}
      </div>
      <p style={{ color: "#64748b", fontSize: "13px" }}>Authors: {authors}</p>
    </div>
  );
}

function PublicationTable({
  items,
  currentPage,
  totalPages,
  totalItems,
  limit,
  onPageChange,
  loading,
}) {
  if (loading) {
    return (
      <p className="fsd-empty text-center py-4">Memuat data publikasi...</p>
    );
  }

  if (!items?.length) {
    return <p className="fsd-empty">No publications match the filters.</p>;
  }

  return (
    <div className="fsd-table-wrap" style={{ marginTop: "20px" }}>
      <table className="fsd-publication-table">
        <thead>
          <tr>
            <th style={{ width: "60px", textAlign: "center" }}>No.</th>
            <th>Publication</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const title = getPublicationTitle(item);
            return (
              <tr key={item?.id || `${title}-${index}`}>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "#576b7b",
                    fontSize: "15px",
                  }}
                >
                  {(currentPage - 1) * limit + index + 1}
                </td>
                <td>{formatPublicationFSD(item)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          borderTop: "1px solid #dce5ee",
          fontSize: "14px",
          color: "#576b7b",
          background: "#f9fbfd",
        }}
      >
        <span>
          Showing {items.length} of {totalItems} records
        </span>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            style={{
              padding: "6px 14px",
              border: "1px solid #dce5ee",
              borderRadius: "4px",
              background: currentPage <= 1 ? "#f1f5f9" : "#fff",
              cursor: currentPage <= 1 ? "not-allowed" : "pointer",
              color: "#33495b",
              fontWeight: "bold",
            }}
          >
            Previous
          </button>
          <span style={{ padding: "0 8px", fontWeight: "bold" }}>
            Page {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            style={{
              padding: "6px 14px",
              border: "1px solid #dce5ee",
              borderRadius: "4px",
              background: currentPage >= totalPages ? "#f1f5f9" : "#fff",
              cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
              color: "#33495b",
              fontWeight: "bold",
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Main() {
  const { alias } = useParams();
  const { API_URL } = useContext(apiContext);
  const [user, setUser] = useState(null);
  const [allData, setAllData] = useState([]);
  const [publications, setPublications] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    jenis: "semua",
    tahun: "semua",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState("");

  const readableName = useMemo(
    () => (alias || "").replaceAll("_", " "),
    [alias],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters.jenis, filters.tahun, limit]);

  useEffect(() => {
    let mounted = true;
    async function fetchProfile() {
      setLoading(true);
      setError("");
      try {
        const [profileResponse, allDataRes] = await Promise.all([
          axios.get(`/user/${alias}`),
          axios.get(`/data-fsd/${alias}`, {
            headers: { "X-API-KEY": process.env.REACT_APP_PUBLICATION_API_KEY },
          }),
        ]);
        if (mounted) {
          setUser(profileResponse.data);
          setAllData(allDataRes.data || []);
        }
      } catch (err) {
        if (mounted) setError("Profile not found.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchProfile();
    return () => (mounted = false);
  }, [alias]);

  useEffect(() => {
    let mounted = true;
    async function fetchPaginatedData() {
      setTableLoading(true);
      try {
        const res = await axios.get(`/user/${alias}/publikasi`, {
          headers: { "X-API-KEY": process.env.REACT_APP_PUBLICATION_API_KEY },
          params: {
            search: debouncedSearch,
            jenis: filters.jenis,
            tahun: filters.tahun,
            page,
            limit,
          },
        });
        if (mounted && res.data?.data) {
          setPublications(res.data.data.data || []);
          setTotalItems(res.data.data.total || 0);
          setTotalPages(res.data.data.last_page || 1);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setTableLoading(false);
      }
    }

    if (user) {
      fetchPaginatedData();
    }
    return () => (mounted = false);
  }, [alias, user, debouncedSearch, filters.jenis, filters.tahun, page, limit]);

  const photoUrl = getPhotoUrl(user, API_URL);
  const profile = user?.profile || {};
  const totalPublikasi = totalItems || allData?.length || 0;
  const publicationYears = useMemo(() => {
    return [...new Set(allData.map(getPublicationYear).filter(Boolean))].sort(
      (a, b) => b - a,
    );
  }, [allData]);
  const publicationTypeOptions = useMemo(() => {
    return [...new Set(allData.map(getPublicationType).filter(Boolean))].sort();
  }, [allData]);

  return (
    <div className="fsd-page" style={{ position: "relative", zIndex: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "480px",
          zIndex: -1,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(#8EA2AE 2px, transparent 2px)",
            backgroundSize: "24px 24px",
            opacity: 0.8,
            maskImage:
              "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
          }}
        ></div>
        <svg
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <path
            fill="#A4B3BB"
            fillOpacity="0.4"
            d="M0,192L48,197.3C96,203,192,213,288,202.7C384,192,480,160,576,149.3C672,139,768,149,864,165.3C960,181,1056,203,1152,192C1248,181,1344,139,1392,117.3L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
          <path
            fill="#10586E"
            fillOpacity="0.7"
            d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,176C1248,171,1344,149,1392,138.7L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
          <path
            fill="#001525"
            fillOpacity="0.9"
            d="M0,64L48,80C96,96,192,128,288,133.3C384,139,480,117,576,112C672,107,768,117,864,122.7C960,128,1056,128,1152,112C1248,96,1344,64,1392,48L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
        </svg>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "480px",
          zIndex: -1,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: 0.4,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(#8EA2AE 2px, transparent 2px)",
            backgroundSize: "24px 24px",
            opacity: 0.8,
            maskImage:
              "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage:
              "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
          }}
        ></div>
        <svg
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transform: "rotate(180deg)",
          }}
        >
          <path
            fill="#A4B3BB"
            fillOpacity="0.4"
            d="M0,192L48,197.3C96,203,192,213,288,202.7C384,192,480,160,576,149.3C672,139,768,149,864,165.3C960,181,1056,203,1152,192C1248,181,1344,139,1392,117.3L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
          <path
            fill="#10586E"
            fillOpacity="0.7"
            d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,176C1248,171,1344,149,1392,138.7L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
          <path
            fill="#001525"
            fillOpacity="0.9"
            d="M0,64L48,80C96,96,192,128,288,133.3C384,139,480,117,576,112C672,107,768,117,864,122.7C960,128,1056,128,1152,112C1248,96,1344,64,1392,48L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
        </svg>
      </div>

      <header
        className="fsd-topbar"
        style={{
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(10px)",
          borderBottomColor: "rgba(216, 225, 234, 0.4)",
        }}
      >
        <Link to={`/${alias || ""}`} className="fsd-brand">
          <img src={unsamLogo} alt="Universitas Samudra" />
        </Link>
        <nav>
          <a href="#home">Home</a>
          <a href="#about">About</a>
          <a href="#publications">Publications</a>
        </nav>
      </header>

      <main id="home" className="fsd-shell">
        {user && (
          <Helmet>
            <title>{fullName(user)} - Faculty Staff Directory</title>
            <meta
              name="description"
              content={`Profil akademik ${fullName(user)} di ${user?.profile?.fakultas?.nama || "Universitas Samudra"}. Fokus riset: ${profile?.fokus || "Akademik"}.`}
            />

            <meta
              property="og:title"
              content={`${fullName(user)} - FSD Unsam`}
            />
            <meta
              property="og:description"
              content={`Lihat profil akademik, publikasi, dan metrik riset ${fullName(user)} di Universitas Samudra.`}
            />
            <meta property="og:image" content={photoUrl || unsamLogo} />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="profile" />
          </Helmet>
        )}
        {loading ? (
          <div className="fsd-state">Loading profile {readableName}...</div>
        ) : error ? (
          <div className="fsd-state fsd-state-error">{error}</div>
        ) : (
          <>
            <section
              className="fsd-hero"
              style={{
                position: "relative",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                boxShadow: "0 10px 30px rgba(0,21,37,0.06)",
                borderBottom: "none",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${texturedBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  zIndex: -1,
                  opacity: 0.07,
                }}
              />
              <div className="fsd-hero-copy">
                <p className="fsd-eyebrow">Faculty Staff Directory</p>
                <h1>{fullName(user)}</h1>
                <p className="fsd-role">Lecturer at Universitas Samudra</p>
                <div className="d-flex flex-column gap-3">
                  <span> {user?.profile?.fakultas?.nama}</span>
                  <a
                    className="text-decoration-none text-muted d-flex gap-2 align-items-center"
                    href={`mailto:${user?.email || ""}`}
                  >
                    <EmailOutlinedIcon fontSize="small" />
                    {user?.email || "Email not available"}
                  </a>
                  <span className=" text-muted d-flex gap-2 align-items-center">
                    <i className="fa fa-search"></i>{" "}
                    {profile?.fokus || "No focus area"}
                  </span>

                  {profile?.sinta_overall != null && profile?.sinta_overall !== "" && (
                    <span className="text-muted d-flex gap-2 align-items-center">
                      <EmojiEventsOutlinedIcon fontSize="small" /> SINTA Score Overall:{" "}
                      <strong>{profile.sinta_overall}</strong>
                    </span>
                  )}
                  {profile?.sinta_3yr != null && profile?.sinta_3yr !== "" && (
                    <span className="text-muted d-flex gap-2 align-items-center">
                      <EmojiEventsOutlinedIcon fontSize="small" /> SINTA Score 3Yr:{" "}
                      <strong>{profile.sinta_3yr}</strong>
                    </span>
                  )}
                </div>
                <div className="d-flex gap-3 mt-4">
                  {user?.profile?.sinta != null ? (
                    <ProfileBadge
                      href={`https://sinta.kemdiktisaintek.go.id/authors/profile/${user?.profile?.sinta}`}
                      image={garudaLogo}
                      label="Sinta"
                    />
                  ) : null}
                  {user?.profile?.scopus != null ? (
                    <ProfileBadge
                      href={`https://www.scopus.com/authid/detail.uri?authorId=${user?.profile?.scopus}`}
                      image={scopusLogo}
                      label="Scopus"
                    />
                  ) : null}
                  {user?.profile?.google != null ? (
                    <ProfileBadge
                      href={`https://scholar.google.com/citations?user=${user?.profile?.google}`}
                      image={googleScholarLogo}
                      label="Google Scholar"
                    />
                  ) : null}
                  {user?.profile?.thomson != null ? (
                    <ProfileBadge
                      href={`https://www.webofscience.com/authid/detail.uri?authorId=${user?.profile?.thomson}`}
                      image={thomsonLogo}
                      label="Thomson Reuters"
                    />
                  ) : null}
                </div>
              </div>
              <aside
                className="fsd-profile-card bg-transparent"
                style={{ borderRadius: "12px", border: "none" }}
              >
                {user.profile ? (
                  <img
                    className="fsd-avatar"
                    style={{ borderRadius: "12px" }}
                    src={user.profile.foto}
                    alt={fullName(user)}
                  />
                ) : (
                  <div
                    className="fsd-avatar fsd-avatar-placeholder"
                    style={{ borderRadius: "12px" }}
                  >
                    {(user?.name || "?").charAt(0)}
                  </div>
                )}
              </aside>
            </section>

            <div className="fsd-layout">
              <article>
                <Section id="about" title="About Me">
                  {profile?.about ? (
                    <p>{profile.about}</p>
                  ) : (
                    <p>
                      {`${fullName(user)} is a lecturer at ${user?.profile.fakultas?.nama || "Universitas Samudra"}. This page displays a summary of their profile, contact information, and academic publications available in the system.`}
                    </p>
                  )}
                </Section>

                {user?.pendidikan?.length > 0 && (
                  <Section id="education" title="Education Background">
                    <div
                      style={{
                        position: "relative",
                        paddingLeft: "20px",
                        borderLeft: "2px solid #dce5ee",
                        marginLeft: "8px",
                        marginTop: "10px",
                      }}
                    >
                      {user.pendidikan.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            marginBottom:
                              index === user.pendidikan.length - 1
                                ? "0"
                                : "28px",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: "-27px",
                              top: "6px",
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              border: "2px solid #0f6a8f",
                            }}
                          ></div>

                          <div
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              backgroundColor: "#f3f7fb",
                              color: "#0f6a8f",
                              fontSize: "12px",
                              fontWeight: "bold",
                              borderRadius: "6px",
                              marginBottom: "8px",
                              border: "1px solid #dce5ee",
                            }}
                          >
                            Class of {item.tahun_lulus || "-"}
                          </div>

                          <h3
                            style={{
                              margin: "0 0 6px",
                              fontSize: "18px",
                              color: "#112f4a",
                              fontWeight: "800",
                            }}
                          >
                            {item.nama_perguruan_tinggi}
                          </h3>

                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "800",
                                color: "#275b7a",
                                fontSize: "14px",
                                backgroundColor: "#eef4f8",
                                padding: "2px 8px",
                                borderRadius: "4px",
                              }}
                            >
                              {item.nama_jenjang}
                            </span>
                            {item.program_studi && (
                              <>
                                <span
                                  style={{ color: "#576b7b", fontSize: "14px" }}
                                >
                                  {item.program_studi}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                <Section id="publications" title="Publications">
                  <div
                    className="fsd-filters"
                    style={{
                      gridTemplateColumns:
                        "minmax(220px, 1fr) 180px 150px 100px auto",
                    }}
                  >
                    <input
                      type="search"
                      placeholder="Search title, DOI, journal..."
                      value={filters.search}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          search: event.target.value,
                        }))
                      }
                    />
                    <select
                      value={filters.jenis}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          jenis: event.target.value,
                        }))
                      }
                    >
                      <option value="semua">All types</option>
                      {publicationTypeOptions.map((type) => (
                        <option value={type} key={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <select
                      value={filters.tahun}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          tahun: event.target.value,
                        }))
                      }
                    >
                      <option value="semua">All years</option>
                      {publicationYears.map((year) => (
                        <option value={year} key={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <select
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      title="Items per page"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <p className="fsd-table-count">
                    Showing {publications.length} of {totalItems} publications.
                  </p>
                  <PublicationTable
                    items={publications}
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    limit={limit}
                    onPageChange={setPage}
                    loading={tableLoading}
                  />
                </Section>
              </article>

              <aside className="fsd-sidebar">
                <div className="fsd-contact-card fsd-pie-chart">
                  <PieChartPublikasi alias={user?.alias} />
                </div>

                <div className="fsd-contact-card fsd-bar-chart">
                  <BarChartQuartile alias={user?.alias} />
                </div>
              </aside>
            </div>
          </>
        )}
      </main>

      <footer
        className="fsd-footer"
        style={{
          background: "rgba(255, 255, 255, 0.4)",
          backdropFilter: "blur(4px)",
          borderTopColor: "rgba(216, 225, 234, 0.4)",
          position: "relative",
          zIndex: 10,
        }}
      >
        FSD (c) 2023-2026. Universitas Samudra. All Rights Reserved.
      </footer>
    </div>
  );
}
